// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

export const RPM_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/rpm';
export const RPM_STATUS_SYSTEM = 'https://polarissleep.com/fhir/rpm-status';
export const RPM_MONTH_IDENTIFIER_SYSTEM = 'https://polarissleep.com/fhir/rpm-month';
export const RPM_CPT_99457 = '99457';

export const RPM_OBSERVATION_CODES = {
  adherenceNights30Day: 'adherence-nights-30-day',
  lastContactDate: 'last-contact-date',
  monitoringStatus: 'monitoring-status',
  asyncMonitoringMinutes: 'async-monitoring-minutes',
  syncContactMinutes: 'sync-contact-minutes',
  monthlyNote: 'rpm-monthly-note',
} as const;

export const RPM_REQUIREMENT_THRESHOLDS = {
  syncMinutes: 5,
  totalMinutes: 20,
} as const;

/** The sync requirement is one connected call or video contact in the month, of any length. */
export function isRpmSyncRequirementMet(syncMinutes: number | undefined): boolean {
  return (syncMinutes ?? 0) > 0;
}

/**
 * A month's RPM requirement is met when async and sync minutes together reach the total threshold
 * and the sync requirement is met. Missing values count as zero.
 */
export function isRpmMonthRequirementMet(asyncMinutes: number | undefined, syncMinutes: number | undefined): boolean {
  return (
    (asyncMinutes ?? 0) + (syncMinutes ?? 0) >= RPM_REQUIREMENT_THRESHOLDS.totalMinutes &&
    isRpmSyncRequirementMet(syncMinutes)
  );
}
export const RPM_ASYNC_ACTIVITY_TYPES = [
  { value: 'data-review', label: 'Data review' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'care-plan-adjustment', label: 'Care plan adjustment' },
] as const;

export const RPM_SYNC_CONTACT_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'video', label: 'Video' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
] as const;

export const RPM_MONITORING_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'adherent', label: 'Adherent' },
  { value: 'low-usage', label: 'Low usage' },
  { value: 'below-threshold', label: 'Below threshold' },
  { value: 'non-responsive', label: 'Non-responsive' },
] as const;

export type RpmAsyncActivityType = (typeof RPM_ASYNC_ACTIVITY_TYPES)[number]['value'];

export type RpmSyncContactMethod = (typeof RPM_SYNC_CONTACT_METHODS)[number]['value'];

export type RpmMonitoringStatus = (typeof RPM_MONITORING_STATUSES)[number]['value'];

export const FOLLOW_UP_CADENCES = [30, 60, 90, 180] as const;

export type FollowUpCadenceDays = (typeof FOLLOW_UP_CADENCES)[number];

export interface CalendarMonthBounds {
  month: string;
  start: Date;
  end: Date;
}

function toValidDate(value: string | Date): Date | undefined {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isFinite(date.getTime()) ? date : undefined;
}

export function getCurrentRpmMonth(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function getCalendarMonthBounds(monthOrDate: string | Date = new Date()): CalendarMonthBounds {
  const month =
    typeof monthOrDate === 'string' && /^\d{4}-\d{2}$/.test(monthOrDate)
      ? monthOrDate
      : getCurrentRpmMonth(toValidDate(monthOrDate) ?? new Date());
  const [yearPart, monthPart] = month.split('-');
  const year = Number(yearPart);
  const monthIndex = Number(monthPart) - 1;
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 1, 0, 0, 0, 0);
  return { month, start, end };
}

export function addDays(value: string | Date, days: number): Date | undefined {
  const date = toValidDate(value);
  if (!date) {
    return undefined;
  }
  date.setDate(date.getDate() + days);
  return date;
}

export function isDateInCalendarMonth(
  value: string | Date | undefined,
  monthOrDate: string | Date = new Date()
): boolean {
  if (!value) {
    return false;
  }
  const date = toValidDate(value);
  if (!date) {
    return false;
  }
  const { start, end } = getCalendarMonthBounds(monthOrDate);
  return date >= start && date < end;
}

export function isDueInMonth(
  treatmentStart: string | Date | undefined,
  cadenceDays: FollowUpCadenceDays,
  monthOrDate: string | Date = new Date()
): boolean {
  if (!treatmentStart) {
    return false;
  }
  const dueDate = addDays(treatmentStart, cadenceDays);
  return isDateInCalendarMonth(dueDate, monthOrDate);
}

export function getAdherenceColor(nights: number): 'green' | 'yellow' | 'red' {
  if (nights >= 21) {
    return 'green';
  }
  if (nights >= 14) {
    return 'yellow';
  }
  return 'red';
}

export function buildRpmMonthIdentifier(patientId: string, month: string, kind: string): string {
  return `Patient/${patientId}|${month}|${kind}`;
}
